
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { saveAs } from "file-saver";

import Loader from "../../components/Loader";
import BlockEditor from "./BlockEditor";
import SignCanvas, { BLOCK_NAMES, HEADER_DEFAULTS } from "./SignCanvas";
import {
    EMPTY_PROFILE_DRAFT,
    EMPTY_SIGN_DRAFT,
    buildSign,
    createProfile,
    createSign,
    deleteProfileLogo,
    deleteSign,
    duplicateSign,
    exportSignJson,
    fetchSignFile,
    getProfile,
    getSign,
    getSignMeta,
    importSignJson,
    listAssets,
    listProfiles,
    previewSign,
    readApiError,
    readJsonFile,
    toProfileDraft,
    toSignDraft,
    updateProfile,
    updateSign,
    uploadProfileLogo,
} from "./printSignApi";
import {
    AssetPicker,
    Choice,
    ColorsEditor,
    Field,
    ImageAdjust,
    JsonImportButton,
    MAX_IMAGE_BYTES,
    Range,
    Section,
    Switch,
    formatBytes,
    formatDateTime,
    sameDraft,
    usePreview,
} from "./signUi";
import { ActionsMenu, PageMessage, SIGNS_ROOT, SignsPage, StatusBadge } from "./signPage";

/*
 * Редактор вывески: слева — все настройки, справа — сама вывеска.
 * На картинке выбираются блоки, тянутся границы, логотип, надпись и фото.
 * Данные фирмы правятся тут же и сохраняются в профиль бренда; профиля нет —
 * картинка рисуется по заготовке, профиль создастся при сохранении.
 */

const BLEEDS = [
    { value: 0, label: "Нет" },
    { value: 3, label: "3 мм", hint: "типография" },
    { value: 5, label: "5 мм", hint: "широкоформат" },
];

const TEMPLATE_NAMES = ["", "Новый профиль"];
const TEMPLATE_BRAND = "Название фирмы";

const toSize = (text) => {
    const number = Number(String(text).replace(/\D/g, "").slice(0, 4));
    return Number.isFinite(number) ? number : 0;
};

const plural = (count, one, few, many) => {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11) return `${count} ${one}`;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} ${few}`;
    return `${count} ${many}`;
};

/** Коротко, что сейчас в блоке, — для заголовка в списке блоков. */
const blockSummary = (type, profileDraft) => {
    if (!profileDraft) return "";
    if (type === "header") return profileDraft.brandName || "без названия";
    if (type === "features") return plural(profileDraft.features.length, "карточка", "карточки", "карточек");
    if (type === "services") return plural(profileDraft.services.length, "услуга", "услуги", "услуг");
    return `${plural(profileDraft.phones.length, "телефон", "телефона", "телефонов")}, ${plural(profileDraft.addresses.length, "адрес", "адреса", "адресов")}`;
};

const fileLabel = (sign, file, format) => (
    `${(sign?.name || "Вывеска").replace(/[\\/:*?"<>|]+/g, " ").trim()} ${file.widthMm}x${file.heightMm}.${format}`
);

/* ------------------------------------------------------------------ */
/* Правая колонка: вывеска с рамками                                   */
/* ------------------------------------------------------------------ */

const VisualPane = ({ preview, subtitle, disabled, canvasProps, blockTools }) => {
    const [frames, setFrames] = useState(true);
    return (
        <aside className="signsEditor__visual">
            <div className="signsEditor__visualHead">
                <div>
                    <strong>Как выйдет в печать</strong>
                    <span>{subtitle}</span>
                </div>
                <div className="signsEditor__visualTools">
                    <Switch checked={frames} onChange={setFrames} label="Рамки" />
                    <Switch checked={preview.auto} onChange={preview.setAuto} label="Обновлять сам" />
                    <button type="button" className="signsButton" disabled={preview.loading || disabled} onClick={preview.refresh}>
                        {preview.loading ? "Рисуем…" : "Обновить"}
                    </button>
                    {preview.url ? <a className="signsButton" href={preview.url} target="_blank" rel="noreferrer">Крупно</a> : null}
                </div>
            </div>

            {frames && blockTools ? blockTools : (
                <p className="signsEditor__hint">
                    {frames
                        ? "Щёлкните блок на картинке — слева откроются его поля. Граница между блоками тянется мышкой."
                        : "Рамки выключены — так вывеска выглядит без подсказок."}
                </p>
            )}

            {preview.error ? <div className="printCatalogs__message is-error">{preview.error}</div> : null}
            {preview.warnings?.length ? (
                <div className="printCatalogs__message is-warning printSigns__warnings">
                    {preview.warnings.map((warning) => <div key={warning}>• {warning}</div>)}
                </div>
            ) : null}

            <div className={`signsEditor__stage${preview.loading ? " is-loading" : ""}`}>
                {preview.url ? (
                    <div className="printSigns__canvas">
                        <img src={preview.url} alt="Вывеска" draggable={false} />
                        {frames ? <SignCanvas url={preview.url} layout={preview.layout} {...canvasProps} /> : null}
                    </div>
                ) : (
                    <div className="signsEditor__stageEmpty">{preview.loading ? <Loader /> : "Нажмите «Обновить» — здесь появится вывеска."}</div>
                )}
            </div>
        </aside>
    );
};

/** Кнопки выбранного блока — над картинкой, чтобы не закрывать саму вывеску. */
const BlockTools = ({ type, index, count, visibleCount, disabled, onMove, onHide, onClose, headerChanged, onHeaderReset, hasPhoto }) => (
    <div className={`signsEditor__blockTools is-${type}`}>
        <i className={`printSigns__blockDot is-${type}`} />
        <strong>{BLOCK_NAMES[type]}</strong>
        <span>
            {type === "header" ? "логотип и надпись тяните мышкой, колесо — больше / меньше"
                : type === "features" && hasPhoto ? "фото тяните мышкой, колесо — увеличение"
                    : "поля блока — слева"}
        </span>
        <button type="button" className="signsButton" title="Выше" disabled={disabled || index <= 0} onClick={() => onMove(type, -1)}>↑</button>
        <button type="button" className="signsButton" title="Ниже" disabled={disabled || index < 0 || index >= count - 1} onClick={() => onMove(type, 1)}>↓</button>
        {type === "header" ? (
            <button type="button" className="signsButton" disabled={disabled || !headerChanged} onClick={onHeaderReset}>↺ Как в дизайне</button>
        ) : null}
        <button type="button" className="signsButton" disabled={disabled || visibleCount <= 1} onClick={() => onHide(type)}>Скрыть</button>
        <button type="button" className="signsButton" title="Снять выбор" onClick={onClose}>×</button>
    </div>
);

/* ------------------------------------------------------------------ */
/* Редактор                                                            */
/* ------------------------------------------------------------------ */

const SignEditorView = ({ signId, onCreated }) => {
    const navigate = useNavigate();
    const [meta, setMeta] = useState(null);
    const [profiles, setProfiles] = useState([]);
    const [assets, setAssets] = useState([]);
    const [sign, setSign] = useState(null);
    const [draft, setDraft] = useState(EMPTY_SIGN_DRAFT);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [profile, setProfile] = useState(null);
    const [profileDraft, setProfileDraft] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [selected, setSelected] = useState("");
    const [busy, setBusy] = useState("");
    const [message, setMessage] = useState(null);
    const mountedRef = useRef(true);
    // Чей профиль сейчас в profileDraft: id, null — заготовка, undefined — ещё не загружен.
    const profileKeyRef = useRef(undefined);
    const blockRefs = useRef({});
    const scrollToRef = useRef("");

    useEffect(() => () => {
        mountedRef.current = false;
    }, []);

    /* ---------- Загрузка ---------- */

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const [nextMeta, nextProfiles, nextAssets, nextSign] = await Promise.all([
                    getSignMeta(),
                    listProfiles(),
                    listAssets(),
                    signId !== "new" ? getSign(signId) : Promise.resolve(null),
                ]);
                if (!alive) return;
                setMeta(nextMeta);
                setProfiles(nextProfiles);
                setAssets(nextAssets);
                if (nextSign) {
                    setSign(nextSign);
                    setDraft(toSignDraft(nextSign));
                } else {
                    setDraft({ ...EMPTY_SIGN_DRAFT, profileId: nextProfiles[0]?._id || null });
                }
            } catch (error) {
                if (alive) setLoadError(await readApiError(error, "Вывеска не открылась"));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
        // Редактор пересоздаётся на каждую вывеску (key), поэтому грузим один раз.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const template = useMemo(() => toProfileDraft({ ...EMPTY_PROFILE_DRAFT, ...(meta?.profileTemplate || {}) }), [meta]);
    const savedSign = useMemo(() => (sign ? toSignDraft(sign) : null), [sign]);
    const savedProfile = useMemo(() => (profile ? toProfileDraft(profile) : template), [profile, template]);
    const signDirty = !savedSign || !sameDraft(draft, savedSign);
    const profileDirty = !!profileDraft && !sameDraft(profileDraft, savedProfile);
    const dirty = signDirty || profileDirty || !profile;
    const locked = !!busy;

    // Профиль вывески: выбранный — с сервера, не выбран — заготовка (её видно на картинке сразу).
    const wantedProfileId = loading || !meta ? undefined : draft.profileId || null;
    useEffect(() => {
        if (wantedProfileId === undefined || wantedProfileId === profileKeyRef.current) return undefined;
        profileKeyRef.current = wantedProfileId;
        if (!wantedProfileId) {
            setProfile(null);
            setProfileDraft(template);
            return undefined;
        }
        let alive = true;
        setProfileLoading(true);
        getProfile(wantedProfileId)
            .then((next) => {
                if (!alive) return;
                setProfile(next);
                setProfileDraft(toProfileDraft(next));
            })
            .catch(async (error) => {
                if (!alive) return;
                // Профиль удалён или недоступен — работаем с заготовкой, при сохранении создастся новый.
                profileKeyRef.current = null;
                setProfile(null);
                setProfileDraft(template);
                setDraft((current) => ({ ...current, profileId: null }));
                setMessage({ type: "error", text: await readApiError(error, "Профиль не открылся — взята заготовка") });
            })
            .finally(() => alive && setProfileLoading(false));
        return () => {
            alive = false;
        };
    }, [wantedProfileId, template]);

    /* ---------- Правки ---------- */

    const patch = useCallback((changes) => {
        setDraft((current) => ({ ...current, ...changes }));
        setMessage(null);
    }, []);

    const patchNested = useCallback((key, changes) => {
        setDraft((current) => ({ ...current, [key]: { ...current[key], ...changes } }));
        setMessage(null);
    }, []);

    const patchProfile = useCallback((changes) => {
        setProfileDraft((current) => ({ ...current, ...changes }));
        setMessage(null);
    }, []);

    const upsertProfile = (next) => setProfiles((current) => (
        current.some((item) => item._id === next._id)
            ? current.map((item) => (item._id === next._id ? { ...item, ...next } : item))
            : [next, ...current]
    ));

    const changeProfile = (nextId) => {
        const id = nextId || null;
        if (id === (draft.profileId || null)) return;
        if (profileDirty && !window.confirm("Несохранённые правки данных фирмы (название, контакты, услуги) пропадут. Переключить профиль?")) return;
        patch({ profileId: id });
    };

    /** Выбрать блок. С картинки — ещё и прокрутить настройки к его полям. */
    const select = useCallback((type, fromCanvas = false) => {
        setSelected(type);
        scrollToRef.current = fromCanvas ? type : "";
    }, []);

    useEffect(() => {
        const type = scrollToRef.current;
        if (!type) return;
        scrollToRef.current = "";
        blockRefs.current[type]?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }, [selected]);

    const moveBlock = (index, delta) => {
        const blocks = [...draft.blocks];
        const target = index + delta;
        if (target < 0 || target >= blocks.length) return;
        [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
        patch({ blocks });
    };

    /** С картинки: поменять местами с соседним видимым блоком. */
    const moveVisible = (type, delta) => {
        const index = draft.blocks.findIndex((block) => block.type === type);
        let target = index + delta;
        while (target >= 0 && target < draft.blocks.length && !draft.blocks[target].visible) target += delta;
        if (index < 0 || target < 0 || target >= draft.blocks.length) return;
        const blocks = [...draft.blocks];
        [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
        patch({ blocks });
    };

    const setVisible = (type, visible) => {
        patch({ blocks: draft.blocks.map((block) => (block.type === type ? { ...block, visible } : block)) });
        if (!visible && selected === type) setSelected("");
    };

    /* ---------- Картинка ---------- */

    const preview = usePreview({
        enabled: !loading && !profileLoading && !!profileDraft,
        deps: { draft, profileDraft, profileId: profile?._id, logo: profile?.logoUpdatedAt },
        request: () => previewSign({
            signId: sign?._id || "",
            sign: draft,
            profileId: profile?._id || "",
            profile: profileDraft,
            width: 1000,
        }),
    });

    /* ---------- Сохранение ---------- */

    const profileName = () => {
        if (!TEMPLATE_NAMES.includes(String(profileDraft.name || "").trim())) return profileDraft.name.trim();
        const brand = String(profileDraft.brandName || "").trim();
        if (brand && brand !== TEMPLATE_BRAND) return brand;
        return draft.name.trim() || "Профиль бренда";
    };

    /** Профиль: нет — создать из того, что на экране; есть и правился — сохранить. */
    const persistProfile = async () => {
        if (!profile) {
            const created = await createProfile({ ...profileDraft, name: profileName() });
            profileKeyRef.current = created._id;
            if (!mountedRef.current) return created;
            // Сначала id в вывеске, потом профиль: так загрузчик профиля не примет его за «чужой».
            setDraft((current) => ({ ...current, profileId: created._id }));
            setProfile(created);
            setProfileDraft(toProfileDraft(created));
            upsertProfile(created);
            return created;
        }
        if (!profileDirty) return profile;
        const updated = await updateProfile(profile._id, profileDraft);
        if (!mountedRef.current) return updated;
        setProfile(updated);
        setProfileDraft(toProfileDraft(updated));
        upsertProfile(updated);
        return updated;
    };

    /** Сохранить всё. Новая вывеска создаётся при первом сохранении (и адрес страницы меняется на её). */
    const persist = async () => {
        const savedProfileDoc = await persistProfile();
        const payload = { ...draft, profileId: savedProfileDoc._id, name: draft.name.trim() || savedProfileDoc.name || "Вывеска" };
        const next = sign ? await updateSign(sign._id, payload) : await createSign(payload);
        if (!mountedRef.current) return next;
        setSign(next);
        setDraft(toSignDraft(next));
        if (!sign) onCreated(next._id);
        return next;
    };

    const run = async (key, task, errorText) => {
        if (busy) return;
        setBusy(key);
        setMessage(null);
        try {
            await task();
        } catch (error) {
            if (mountedRef.current) setMessage({ type: "error", text: await readApiError(error, errorText) });
        } finally {
            if (mountedRef.current) setBusy("");
        }
    };

    const save = () => run("save", async () => {
        const hadProfile = !!profile;
        const changedProfile = profileDirty;
        await persist();
        if (!mountedRef.current) return;
        setMessage({
            type: "success",
            text: !hadProfile
                ? "Сохранено. Создан профиль бренда — его можно выбрать и в других вывесках."
                : `Сохранено.${changedProfile ? " Данные фирмы обновились во всех вывесках с этим профилем — их файлы соберите заново." : ""}`,
        });
    }, "Не удалось сохранить");

    const build = () => run("build", async () => {
        const current = dirty || !sign ? await persist() : sign;
        const next = await buildSign(current._id);
        if (!mountedRef.current) return;
        setSign(next);
        const last = next.files[0];
        setMessage({
            type: last?.warnings?.length ? "info" : "success",
            text: last?.warnings?.length ? `Собрано. Обратите внимание: ${last.warnings.join(" ")}` : "Готово: PDF для печати и PNG для мессенджеров — внизу слева, в «Готовых файлах».",
        });
    }, "Не удалось собрать вывеску");

    const download = (file, format) => sign && run(`download:${file._id}:${format}`, async () => {
        saveAs(await fetchSignFile(sign._id, file._id, format), fileLabel(sign, file, format));
    }, "Не удалось скачать файл");

    const copy = () => sign && run("copy", async () => {
        const next = await duplicateSign(sign._id);
        navigate(`${SIGNS_ROOT}/${next._id}`);
    }, "Не удалось скопировать");

    const remove = () => {
        if (!sign || busy) return;
        if (!window.confirm(`Удалить вывеску «${sign.name}»? Собранные файлы тоже удалятся. Профиль бренда останется.`)) return;
        run("delete", async () => {
            await deleteSign(sign._id);
            leaveRef.current = true;
            navigate(SIGNS_ROOT);
        }, "Не удалось удалить");
    };

    /* ---------- Логотип ---------- */

    const pickLogo = (file) => {
        if (file.size > MAX_IMAGE_BYTES) {
            setMessage({ type: "error", text: "Файл больше 30 МБ." });
            return;
        }
        run("logo", async () => {
            const target = await persistProfile();
            const next = await uploadProfileLogo(target._id, file);
            if (!mountedRef.current) return;
            // Сервер меняет только логотип — правки на экране не трогаем.
            setProfile((current) => ({ ...(current || target), hasLogo: next.hasLogo, logoUpdatedAt: next.logoUpdatedAt }));
        }, "Не удалось загрузить логотип");
    };

    const removeLogo = () => profile && run("logo", async () => {
        const next = await deleteProfileLogo(profile._id);
        if (mountedRef.current) setProfile((current) => ({ ...current, hasLogo: next.hasLogo, logoUpdatedAt: next.logoUpdatedAt }));
    }, "Не удалось убрать логотип");

    /* ---------- JSON ---------- */

    const exportJson = () => sign && run("export", async () => {
        const { blob, filename } = await exportSignJson(sign._id);
        saveAs(blob, filename);
        if (mountedRef.current && (signDirty || profileDirty)) {
            setMessage({ type: "info", text: "В файл ушло сохранённое — несохранённые правки в него не попали. Сохраните и выгрузите ещё раз." });
        }
    }, "Не удалось выгрузить JSON");

    /** JSON в эту вывеску (и её профиль); у новой вывески — профиль и вывеска из файла. */
    const importJson = async (file) => {
        if (busy) return;
        const question = sign
            ? `Загрузить «${file.name}» в вывеску «${sign.name}»? Поля из файла заменят текущие${profile ? ` (данные фирмы — в профиле «${profile.name}», он общий для всех его вывесок)` : ""}.`
            : `Создать профиль и вывеску из «${file.name}»?`;
        if (!window.confirm(question)) return;
        await run("import", async () => {
            const result = await importSignJson({ data: await readJsonFile(file), signId: sign?._id || "" });
            if (!mountedRef.current) return;
            if (result.profile) {
                profileKeyRef.current = result.profile._id;
                upsertProfile(result.profile);
            }
            if (result.sign) {
                setSign(result.sign);
                setDraft(toSignDraft(result.sign));
                if (!sign) onCreated(result.sign._id);
            }
            if (result.profile) {
                setProfile(result.profile);
                setProfileDraft(toProfileDraft(result.profile));
            }
            const notes = result.notes?.length ? ` ${result.notes.join(" ")}` : "";
            setMessage({ type: notes ? "info" : "success", text: `JSON загружен и сохранён.${notes}` });
        }, "Не удалось загрузить JSON");
    };

    // Уходим с несохранёнными правками — браузер переспросит.
    const leaveRef = useRef(false);
    const hasUnsaved = (!!sign && (signDirty || profileDirty)) || (!sign && profileDirty);
    useEffect(() => {
        if (!hasUnsaved) return undefined;
        const onBeforeUnload = (event) => {
            if (leaveRef.current) return;
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [hasUnsaved]);

    const goBack = () => {
        if (hasUnsaved && !window.confirm("Есть несохранённые правки. Выйти без сохранения?")) return;
        navigate(SIGNS_ROOT);
    };

    /* ---------- Вёрстка ---------- */

    const title = sign ? sign.name || "Вывеска" : "Новая вывеска";
    const shell = (body, actions = null) => (
        <SignsPage title={title} wide back={SIGNS_ROOT} actions={actions}>
            {body}
        </SignsPage>
    );

    if (loading || (!profileDraft && !loadError)) return shell(<div className="signsEditor__placeholder"><Loader /></div>);
    if (loadError) return shell(<div className="signsEditor__placeholder">{loadError}</div>);

    const presets = (meta?.sizePresets || []).map((preset) => ({
        value: preset.key,
        label: preset.label,
        hint: preset.widthMm ? `${preset.widthMm} × ${preset.heightMm}` : "в мм",
    }));
    const designs = (meta?.designs || []).map((design) => ({ value: design.key, label: design.label, hint: design.hint }));
    const blockLabels = (meta?.blocks || []).reduce((result, block) => ({ ...result, [block.type]: block.label }), { ...BLOCK_NAMES });
    const isCustom = draft.size.preset === "custom";
    const visibleCount = draft.blocks.filter((block) => block.visible).length;
    const customHeights = !!draft.layout?.weights;
    const last = sign?.files?.[0];

    const canvasProps = {
        selected,
        onSelect: (type) => select(type, true),
        onResize: (weights) => patchNested("layout", { weights }),
        imageSettings: draft.featuresImage,
        hasImage: !!draft.featuresImage.assetId,
        onImageChange: (changes) => patchNested("featuresImage", changes),
        header: draft.header,
        onHeaderChange: (changes) => patchNested("header", changes),
        disabled: locked,
    };
    const shownBoxes = preview.layout?.boxes || [];
    const blockTools = selected ? (
        <BlockTools
            type={selected}
            index={shownBoxes.findIndex((box) => box.type === selected)}
            count={shownBoxes.length}
            visibleCount={visibleCount}
            disabled={locked}
            hasPhoto={!!draft.featuresImage.assetId}
            onMove={moveVisible}
            onHide={(type) => setVisible(type, false)}
            onClose={() => setSelected("")}
            headerChanged={Object.keys(HEADER_DEFAULTS).some((key) => Number(draft.header?.[key] ?? HEADER_DEFAULTS[key]) !== HEADER_DEFAULTS[key])}
            onHeaderReset={() => patchNested("header", { ...HEADER_DEFAULTS })}
        />
    ) : null;

    const actions = (
        <>
            <JsonImportButton className="signsButton" disabled={locked} onFile={importJson} label={busy === "import" ? "Загрузка…" : "Импорт JSON"} />
            <ActionsMenu
                label="Ещё"
                disabled={locked}
                items={[
                    { label: "Экспорт JSON", disabled: !sign, onClick: exportJson },
                    { label: "Сделать копию", disabled: !sign, onClick: copy },
                    profile && { label: "Открыть профиль бренда", onClick: () => navigate(`${SIGNS_ROOT}/profiles/${profile._id}`) },
                    { label: "Удалить вывеску", danger: true, divider: true, disabled: !sign, onClick: remove },
                ]}
            />
            <button type="button" className="signsButton" disabled={locked || (!dirty && !!sign)} onClick={save}>
                {busy === "save" ? "Сохранение…" : dirty || !sign ? "Сохранить" : "Сохранено"}
            </button>
            <button type="button" className="signsButton is-primary" disabled={locked} onClick={build}>
                {busy === "build" ? "Собираем…" : last ? "Собрать заново" : "Собрать PDF"}
            </button>
        </>
    );

    return (
        <SignsPage
            title={title}
            subtitle={sign
                ? `${last ? "Собрана" : "Не собрана"} · изменил ${sign.updatedByName || sign.createdByName || "—"} · ${formatDateTime(sign.updatedAt || sign.createdAt)}`
                : "Справа — сразу вывеска. Правьте блоки слева или щёлкайте прямо по картинке."}
            wide
            onBack={goBack}
            actions={actions}
        >
            <PageMessage message={message} onClose={() => setMessage(null)} />

            <div className="signsEditor">
                <div className="signsEditor__settings">
                    <Section title="Вывеска">
                        <Field label="Название (для себя, на вывеску не попадает)" value={draft.name} maxLength={120} disabled={locked}
                            placeholder="Например: Дверь офиса, Кировск" onChange={(name) => patch({ name })} />
                        <div className="printCatalogs__label">Профиль бренда — название, логотип, контакты, услуги</div>
                        <div className="printSigns__profileRow">
                            <select value={draft.profileId || ""} disabled={locked || profileLoading} onChange={(event) => changeProfile(event.target.value)}>
                                <option value="">{profiles.length ? "— Новый профиль (создастся при сохранении) —" : "Новый профиль (создастся при сохранении)"}</option>
                                {profiles.map((item) => (
                                    <option key={item._id} value={item._id}>{item.name}{item.brandName && item.brandName !== item.name ? ` · ${item.brandName}` : ""}</option>
                                ))}
                            </select>
                        </div>
                        <p className="printCatalogs__hint">
                            {profile
                                ? "Данные фирмы правятся прямо здесь, в блоках, и сохраняются в этот профиль — он общий для всех его вывесок."
                                : "Профиля пока нет: на картинке заготовка. Впишите свои данные в блоки (или загрузите JSON) — при сохранении создастся профиль."}
                        </p>
                    </Section>

                    <Section title="Блоки" hint="Щёлкните блок — откроются его поля. Порядок сверху вниз (у широкой вывески — по двум колонкам).">
                        <div className="printSigns__blockList">
                            {draft.blocks.map((block, index) => {
                                const open = selected === block.type;
                                return (
                                    <div key={block.type}
                                        ref={(element) => {
                                            blockRefs.current[block.type] = element;
                                        }}
                                        className={`printSigns__blockCard is-${block.type}${open ? " is-open" : ""}${block.visible ? "" : " is-off"}`}>
                                        <div className="printSigns__blockHead">
                                            <button type="button" className="printSigns__blockTitle" onClick={() => select(open ? "" : block.type)}>
                                                <i className={`printSigns__blockDot is-${block.type}`} />
                                                <span>
                                                    <strong>{blockLabels[block.type] || block.type}</strong>
                                                    <em>{block.visible ? blockSummary(block.type, profileDraft) : "скрыт — на вывеску не попадает"}</em>
                                                </span>
                                                <b title={open ? "Свернуть" : "Править"}>{open ? "▴" : "▾"}</b>
                                            </button>
                                            <div className="printSigns__blockTools">
                                                <Switch checked={block.visible} disabled={locked || (block.visible && visibleCount <= 1)} label="На вывеске"
                                                    onChange={(visible) => setVisible(block.type, visible)} />
                                                <button type="button" title="Выше" disabled={locked || index === 0} onClick={() => moveBlock(index, -1)}>↑</button>
                                                <button type="button" title="Ниже" disabled={locked || index === draft.blocks.length - 1} onClick={() => moveBlock(index, 1)}>↓</button>
                                            </div>
                                        </div>
                                        {open ? (
                                            <BlockEditor
                                                type={block.type}
                                                draft={draft}
                                                patch={patch}
                                                patchNested={patchNested}
                                                profileDraft={profileDraft}
                                                patchProfile={patchProfile}
                                                profile={profile}
                                                meta={meta}
                                                assets={assets}
                                                locked={locked}
                                                logoBusy={busy === "logo"}
                                                onLogoPick={pickLogo}
                                                onLogoRemove={removeLogo}
                                                onAssetUploaded={(asset) => setAssets((current) => [asset, ...current])}
                                                setMessage={setMessage}
                                            />
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>

                        <Range label="Размер текста" value={draft.layout?.textScale || 1} min={0.7} max={1.4} step={0.05} disabled={locked} resetTo={1}
                            format={(value) => `${Math.round(value * 100)}%`}
                            onChange={(textScale) => patchNested("layout", { textScale })} />
                        <div className="printSigns__heightsRow">
                            <span>Высота блоков: {customHeights ? "своя (меняли на картинке)" : "как задумано дизайном"}</span>
                            <button type="button" className="printSigns__add" disabled={locked || !customHeights}
                                onClick={() => patchNested("layout", { weights: null })}>
                                Сбросить
                            </button>
                        </div>
                    </Section>

                    <Section title="Дизайн" hint="Два готовых оформления или свои тона: фон, металл и текст.">
                        <Choice options={designs} value={draft.design} disabled={locked} onChange={(design) => patch({ design })} />
                        {draft.design === "custom" ? (
                            <ColorsEditor value={draft.colors} meta={meta} disabled={locked} onChange={(changes) => patchNested("colors", changes)} />
                        ) : null}
                    </Section>

                    <Section title="Размер" hint="Размер готовой вывески. Вылеты — запас фона за край под обрезку, их добавит сервер.">
                        <Choice options={presets} value={draft.size.preset} disabled={locked}
                            onChange={(preset) => {
                                const found = (meta?.sizePresets || []).find((item) => item.key === preset);
                                patchNested("size", found && found.widthMm
                                    ? { preset, widthMm: found.widthMm, heightMm: found.heightMm }
                                    : { preset });
                            }} />
                        {isCustom ? (
                            <div className="printCatalogs__range printSigns__sizeRow">
                                <span>Ширина</span>
                                <input type="text" inputMode="numeric" value={draft.size.widthMm || ""} disabled={locked}
                                    onChange={(event) => patchNested("size", { widthMm: toSize(event.target.value) })} />
                                <span>× высота</span>
                                <input type="text" inputMode="numeric" value={draft.size.heightMm || ""} disabled={locked}
                                    onChange={(event) => patchNested("size", { heightMm: toSize(event.target.value) })} />
                                <span>мм</span>
                            </div>
                        ) : null}
                        <div className="printCatalogs__label">Вылеты под обрезку</div>
                        <Choice options={BLEEDS} value={draft.size.bleedMm} disabled={locked} onChange={(bleedMm) => patchNested("size", { bleedMm })} />
                        <Switch checked={draft.size.cropMarks} disabled={locked} label="Метки реза в PDF (для типографии)"
                            onChange={(cropMarks) => patchNested("size", { cropMarks })} />
                    </Section>

                    <Section title="Фон вывески" hint="Своя картинка на весь лист (текстура, фото). Без неё — фон дизайна.">
                        <AssetPicker kind="background" assets={assets} value={draft.background.assetId} disabled={locked}
                            emptyLabel="Фон дизайна" setMessage={setMessage} onUploaded={(asset) => setAssets((current) => [asset, ...current])}
                            onChange={(assetId) => patchNested("background", { assetId })} />
                        {draft.background.assetId ? (
                            <ImageAdjust value={draft.background} disabled={locked} onChange={(value) => patchNested("background", value)} />
                        ) : null}
                    </Section>

                    {sign ? (
                        <Section title="Готовые файлы" hint="PDF — для печати (точно в размер, с вылетами). PNG — посмотреть и отправить в мессенджер. Хранятся пять последних сборок.">
                            {sign.files.length ? (
                                <div className="signsFiles is-inline">
                                    {sign.files.map((file, index) => (
                                        <div key={file._id} className="signsFiles__row">
                                            <div>
                                                <strong>
                                                    {index === 0 ? <StatusBadge tone="success">последняя</StatusBadge> : null} {formatDateTime(file.createdAt)}
                                                </strong>
                                                <span>
                                                    {file.widthMm} × {file.heightMm} мм{file.bleedMm ? ` + вылеты ${file.bleedMm} мм` : ""}
                                                    {" · "}PDF {formatBytes(file.pdfBytes)} · {file.createdByName || "—"}
                                                </span>
                                                {file.warnings?.length ? <span className="printCatalogs__status is-warning">{file.warnings.join(" ")}</span> : null}
                                            </div>
                                            <div className="signsFiles__actions">
                                                <button type="button" className="signsButton" disabled={!!busy} onClick={() => download(file, "pdf")}>
                                                    {busy === `download:${file._id}:pdf` ? "…" : "PDF"}
                                                </button>
                                                {file.hasPng ? (
                                                    <button type="button" className="signsButton" disabled={!!busy} onClick={() => download(file, "png")}>
                                                        {busy === `download:${file._id}:png` ? "…" : "PNG"}
                                                    </button>
                                                ) : null}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="printCatalogs__hint">Пока ни одной сборки — кнопка «Собрать PDF» сверху справа.</p>
                            )}
                        </Section>
                    ) : null}
                </div>

                <VisualPane
                    preview={preview}
                    disabled={!profileDraft}
                    canvasProps={canvasProps}
                    blockTools={blockTools}
                    subtitle={`${draft.size.widthMm} × ${draft.size.heightMm} мм${draft.size.bleedMm ? ` + вылеты ${draft.size.bleedMm} мм` : ""} · по тому, что сейчас на экране`}
                />
            </div>
        </SignsPage>
    );
};

/**
 * Страница редактора. Новая вывеска после первого сохранения получает свой адрес,
 * но редактор при этом не пересоздаётся (правки и картинка остаются на месте).
 */
const PrintSignEditorPage = () => {
    const { signId = "new" } = useParams();
    const navigate = useNavigate();
    const createdRef = useRef("");
    // Только что созданная вывеска остаётся тем же редактором, что был у «новой».
    const key = signId === "new" || signId === createdRef.current ? "new" : signId;

    return (
        <SignEditorView
            key={key}
            signId={signId}
            onCreated={(id) => {
                createdRef.current = id;
                navigate(`${SIGNS_ROOT}/${id}`, { replace: true });
            }}
        />
    );
};

export default PrintSignEditorPage;
